// CharacterProperties — the launcher's per-character Properties panel (ENG-1032).
//
// Why it exists: the launcher lists every character an org can see, and nobody
// could tell which ones were still needed. "never", "dummy01" and friends sat
// there indefinitely because deleting one meant guessing whether somebody was
// using it. This panel answers who made it, who last changed it, and — the
// field that actually settles the question — when anything about it was last
// touched at all.
//
// Pure presentational, like CharacterLauncher: props in, one callback out. The
// provenance is read straight off the character row the launcher already has,
// and turned into rows by src/lib/characterProperties.js — all the decisions
// about absent values live there, unit-tested, rather than in JSX.
//
// Styling follows CharacterLauncher's rule, not CharacterCreationWizard's: only
// --m-* tokens, so a palette switch carries. (The wizard's inlined hex predates
// that rule; copying it here would spread it.)

import React, { useEffect, useState } from 'react'
import { characterPropertyRows, originRow } from '../lib/characterProperties.js'
import {
  SHARING, sharingState, sharingPropertyRows, canChangeSharing, unshareWarning,
  isOwnerless, canAssignOwner,
} from '../lib/characterSharing.js'
import { PipelineGlyph, STATUS, MONO, UI_FONT } from './PipelineGlyph.jsx'
import HoldToConfirm from './HoldToConfirm.jsx'
import FloatingWindow from './FloatingWindow.jsx'
import { loadCharacterLook, saveCharacterBackdrop, clearCharacterLook } from '../lib/characterLookApi.js'
import { BACKDROP_SKY } from '../lib/sceneColorDefaults.js'

const LINE = 'var(--m-line)'

/** One label/value pair. An unknown value is shown DISABLED WITH ITS REASON —
    never hidden, and never blank. Hiding it would leave the reader unable to
    tell "nobody recorded this" from "the panel is broken", and this panel is
    the only place that absence gets explained (a card has no room). */
function Row({ row }) {
  return (
    <div
      data-testid={row.testid ?? `char-prop-${row.key}`}
      data-unknown={row.unknown ? '1' : '0'}
      style={{
        display: 'grid', gridTemplateColumns: '116px 1fr', gap: 12,
        alignItems: 'baseline', padding: '7px 0',
        borderBottom: `1px solid ${LINE}`,
      }}
    >
      <span style={{
        fontFamily: UI_FONT, fontSize: 9.5, letterSpacing: '.08em',
        color: 'var(--m-fg-3)', whiteSpace: 'nowrap',
      }}>{row.label}</span>
      <span
        title={row.title ?? undefined}
        style={{
          fontFamily: MONO, fontSize: 10.5, wordBreak: 'break-word',
          // Dimmed and italic so an unknown reads as an absence at a glance,
          // while still being legible enough to act on.
          color: row.unknown ? 'var(--m-fg-3)' : 'var(--m-fg)',
          fontStyle: row.unknown ? 'italic' : 'normal',
          cursor: row.title ? 'help' : 'default',
        }}
      >{row.value}</span>
    </div>
  )
}

/** Ownership assignment (ENG-1028).
 *
 *  This is the cleanup path for every character that predates the feature: they
 *  are shared and OWNERLESS, because nothing in the database records who made
 *  them and migration 00041 declined to invent an answer. Until a character has
 *  an owner nobody can take it private, so this is the step that unblocks the
 *  rest of the feature for the existing catalogue.
 *
 *  ANY org member may claim an ownerless character for themselves. Admin-only
 *  claiming was the original rule and it bricked the feature: every character
 *  predating 00041 is ownerless, an ownerless character cannot be made private,
 *  so if no account holds the role then nothing in the existing catalogue can
 *  ever be cleaned up. The server enforces the same rule; this only presents it.
 *
 *  Only claiming is offered, not a pick-any-user list. Assigning to
 *  SOMEBODY ELSE needs the org's member list, which lives on the auth-service at
 *  /admin/users — and `/admin` in this SPA is already Convorcher's namespace, so
 *  reaching it means an absolute cross-origin call whose CORS allowance is
 *  unverified. A picker that silently fails to load its options is worse than no
 *  picker, so that half waits until the route is confirmed.
 */
function OwnerControl({ character, viewerId, viewerEmail, isOrgAdmin, busy, error, onAssignOwner }) {
  const { allowed, reason, isClaim } = canAssignOwner(character, viewerId, isOrgAdmin)

  // Nothing at all when the server predates the feature, or when the character
  // already HAS an owner. The second is true even for an admin: reassignment
  // needs the org member list this app cannot reach yet (see above), so the
  // alternative would be a button that cannot work. The Owner row already names
  // who owns it, which is the useful half.
  if (sharingState(character) === SHARING.UNKNOWN) return null
  if (!isOwnerless(character)) return null

  return (
    <div data-testid="char-owner-control" style={{ marginTop: 14 }}>
      <div style={{
        fontFamily: UI_FONT, fontSize: 9, letterSpacing: '.14em',
        color: 'var(--m-fg-3)', marginBottom: 6,
      }}>OWNER</div>
      <button
        type="button"
        data-testid="char-take-ownership"
        disabled={busy || !allowed}
        onClick={() => onAssignOwner(viewerId, viewerEmail)}
        style={{
          all: 'unset', boxSizing: 'border-box',
          cursor: busy ? 'progress' : 'pointer',
          padding: '5px 11px', borderRadius: 4,
          fontFamily: UI_FONT, fontSize: 9, letterSpacing: '.08em',
          color: 'var(--m-fg)', border: '1px solid var(--m-line-hi)',
          opacity: busy || !allowed ? 0.6 : 1,
        }}
      >{busy ? 'CLAIMING…' : isClaim ? 'CLAIM THIS CHARACTER' : 'TAKE OWNERSHIP'}</button>
      <div style={{
        marginTop: 8, fontFamily: UI_FONT, fontSize: 9, lineHeight: 1.55,
        color: error ? 'var(--m-danger)' : 'var(--m-fg-3)',
      }}>
        {error ?? reason ?? 'Nobody owns this character. Claiming it does not change who can see it — ' +
          'it stays shared with the org, and you can then make it private.'}
      </div>
    </div>
  )
}

/** The visibility control (ENG-1028).
 *
 *  Publishing is a plain button: it ADDS access, and an accidental publish is
 *  undone by un-sharing. Un-sharing is a HoldToConfirm, because it removes the
 *  character and every one of its assets from everyone else in the org the
 *  instant it returns — destructive in effect even though nothing is deleted,
 *  which is exactly the category HoldToConfirm exists for.
 *
 *  The blast radius is fetched when the panel opens, not when the button is
 *  pressed: a hold that has already begun is the wrong moment to start waiting
 *  on a request, and the numbers are what make the decision informed.
 */
function VisibilityControl({ character, viewerId, isOrgAdmin, impact, busy, error, onSetShared }) {
  const state = sharingState(character)
  const { allowed, reason } = canChangeSharing(character, viewerId, isOrgAdmin)

  if (state === SHARING.UNKNOWN) return null

  const note = (text, tone) => (
    <div style={{
      marginTop: 8, fontFamily: UI_FONT, fontSize: 9, lineHeight: 1.55,
      color: tone === 'error' ? 'var(--m-danger)' : 'var(--m-fg-3)',
    }}>{text}</div>
  )

  return (
    <div data-testid="char-visibility-control" style={{ marginTop: 14 }}>
      <div style={{
        fontFamily: UI_FONT, fontSize: 9, letterSpacing: '.14em',
        color: 'var(--m-fg-3)', marginBottom: 6,
      }}>VISIBILITY</div>

      {!allowed && note(reason)}

      {allowed && state === SHARING.PRIVATE && (
        <>
          <button
            type="button"
            data-testid="char-share-button"
            disabled={busy}
            onClick={() => onSetShared(true)}
            style={{
              all: 'unset', boxSizing: 'border-box',
              cursor: busy ? 'progress' : 'pointer',
              padding: '5px 11px', borderRadius: 4,
              fontFamily: UI_FONT, fontSize: 9, letterSpacing: '.08em',
              color: 'var(--m-fg)', border: `1px solid var(--m-line-hi)`,
              opacity: busy ? 0.6 : 1,
            }}
          >{busy ? 'SHARING…' : 'SHARE WITH THE ORG'}</button>
          {note('Everyone in the org will be able to see and edit this character.')}
        </>
      )}

      {allowed && state === SHARING.SHARED && (
        <>
          <HoldToConfirm
            onConfirm={() => onSetShared(false)}
            title="Hold to make this character private"
            testId="char-unshare-hold"
            confirmId={`unshare:${character.id}`}
            style={{
              padding: '5px 11px', borderRadius: 4,
              fontFamily: UI_FONT, fontSize: 9, letterSpacing: '.08em',
              color: 'var(--m-fg)', border: `1px solid var(--m-line-hi)`,
            }}
          >{busy ? 'MAKING PRIVATE…' : 'HOLD TO MAKE PRIVATE'}</HoldToConfirm>
          {note(unshareWarning(impact))}
        </>
      )}

      {error && note(error, 'error')}
    </div>
  )
}

/**
 * Props:
 *   character — the row from GET /api/characters whose properties to show.
 *   status    — 'rig'|'splat'|'stream'|'missing'|'unknown' from probeRigStatuses(),
 *               so the panel names the same pipeline the card's glyph draws.
 *   now       — epoch ms for relative labels; injectable for tests.
 *   viewerId  — the signed-in user's auth id, for "is this mine".
 *   isOrgAdmin — whether the viewer holds org-admin/super-admin.
 *   loadImpact(slug) — async; fetches the un-share blast radius. Optional: when
 *               absent the control still works and the warning stays generic.
 *   onSetShared(slug, shared) — async; performs the change. Optional: absent
 *               hides the control entirely rather than showing a dead button.
 *   onClose() — dismiss.
 */
/**
 * BACKDROP — the character's saved background, editable from here.
 *
 * Why the colour is editable off-screen but the camera is not: a colour is a
 * value you can pick with no viewport, while a camera pose is only meaningful
 * against what you can see. So this edits the background and Settings ▸ Scene
 * captures the camera; both write the same Scene Look group in the character's
 * event blueprint (src/lib/sceneLookNodes.js).
 *
 * This panel is otherwise purely presentational, so the I/O lives in
 * characterLookApi.js and the state is local to this one section — nothing else
 * in the panel is affected by a load failure here.
 */
function BackdropControl({ slug }) {
  const [state, setState] = useState({ phase: 'loading', backdrop: null, error: null, busy: false })

  useEffect(() => {
    let alive = true
    setState({ phase: 'loading', backdrop: null, error: null, busy: false })
    loadCharacterLook(slug)
      .then(({ backdrop }) => { if (alive) setState({ phase: 'ready', backdrop, error: null, busy: false }) })
      // A read failure is NOT "no saved look": rendering it as one would invite
      // the user to overwrite a background that is actually there.
      .catch(err => { if (alive) setState({ phase: 'error', backdrop: null, error: err?.message ?? String(err), busy: false }) })
    return () => { alive = false }
  }, [slug])

  const apply = async (patch) => {
    setState(s => ({ ...s, busy: true, error: null }))
    const r = await saveCharacterBackdrop(slug, patch)
    setState(s => ({
      ...s, busy: false,
      backdrop: r.ok ? r.backdrop : s.backdrop,
      error: r.ok ? null : r.message,
    }))
  }

  const clear = async () => {
    setState(s => ({ ...s, busy: true, error: null }))
    const r = await clearCharacterLook(slug)
    setState(s => ({ ...s, busy: false, backdrop: r.ok ? null : s.backdrop, error: r.ok ? null : r.message }))
  }

  const b = state.backdrop
  const on = !!b?.skyEnabled
  const color = b?.skyColor ?? BACKDROP_SKY

  return (
    <div data-testid="char-backdrop-control" style={{ marginTop: 14 }}>
      <div style={{
        fontFamily: UI_FONT, fontSize: 9, letterSpacing: '.14em',
        color: 'var(--m-fg-3)', marginBottom: 6,
      }}>BACKDROP</div>

      {state.phase === 'loading' ? (
        <div style={{ fontFamily: UI_FONT, fontSize: 9, color: 'var(--m-fg-3)' }}>reading this character…</div>
      ) : state.phase === 'error' ? (
        // Loud and specific. Never a silent empty state.
        <div style={{ fontFamily: UI_FONT, fontSize: 9, color: 'var(--m-danger)', lineHeight: 1.55 }}>
          couldn’t read this character’s blueprint — {state.error}
        </div>
      ) : (
        <>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <label style={{ display: 'flex', alignItems: 'center', gap: 6, cursor: state.busy ? 'progress' : 'pointer' }}>
              <input
                type="checkbox"
                data-testid="char-backdrop-enabled"
                checked={on}
                disabled={state.busy}
                onChange={e => apply({ skyEnabled: e.target.checked, skyColor: color })}
              />
              <span style={{ fontFamily: UI_FONT, fontSize: 9, letterSpacing: '.08em', color: 'var(--m-fg-2)' }}>
                BACKGROUND
              </span>
            </label>
            <input
              type="color"
              data-testid="char-backdrop-color"
              value={color}
              disabled={state.busy}
              // Commit on `change` (release / picker close), not `input`: the
              // native picker fires input continuously while dragging, and each
              // one here is a blueprint round-trip.
              onChange={e => apply({ skyEnabled: true, skyColor: e.target.value })}
              style={{ width: 40, height: 20, padding: 0, background: 'none', border: '1px solid var(--m-line-hi)' }}
            />
            {b && (
              <button
                type="button"
                data-testid="char-backdrop-clear"
                disabled={state.busy}
                onClick={clear}
                style={{
                  all: 'unset', cursor: state.busy ? 'progress' : 'pointer',
                  padding: '3px 8px', borderRadius: 4, fontFamily: UI_FONT, fontSize: 9,
                  letterSpacing: '.08em', color: 'var(--m-fg-3)', border: '1px solid var(--m-line)',
                }}
              >CLEAR</button>
            )}
          </div>
          <div style={{
            marginTop: 8, fontFamily: UI_FONT, fontSize: 9, lineHeight: 1.55,
            color: state.error ? 'var(--m-danger)' : 'var(--m-fg-3)',
          }}>
            {state.error
              ? state.error
              : b
                ? 'Saved on this character, so it loads this way for everyone. No environment needed. Camera framing is saved from Settings ▸ Scene while the character is open.'
                : 'This character has no saved background. Pick a colour to give it one.'}
          </div>
        </>
      )}
    </div>
  )
}

export default function CharacterProperties({
  character, status = 'unknown', now, onClose,
  viewerId, viewerEmail, isOrgAdmin = false, loadImpact, onSetShared, onAssignOwner,
}) {
  // Escape closes, matching every other overlay in the app. Bound on the
  // document because focus may be on the scrim rather than a control.
  useEffect(() => {
    const onKey = e => { if (e.key === 'Escape') { e.stopPropagation(); onClose?.() } }
    document.addEventListener('keydown', onKey)
    return () => document.removeEventListener('keydown', onKey)
  }, [onClose])

  const [impact, setImpact] = useState(null)
  const [busy, setBusy] = useState(false)
  const [shareError, setShareError] = useState(null)
  const [ownerBusy, setOwnerBusy] = useState(false)
  const [ownerError, setOwnerError] = useState(null)

  const slug = character?.id
  const shared = character?.shared

  // Fetch the blast radius for a SHARED character as soon as the panel opens —
  // the un-share control's warning is the whole point of the confirmation, and
  // starting the request on mousedown would leave the hold racing a fetch.
  // A private character has nothing to lose by being published, so no request.
  useEffect(() => {
    if (!slug || shared !== true || !loadImpact) { setImpact(null); return }
    let live = true
    loadImpact(slug)
      .then(res => { if (live) setImpact(res) })
      // A failed impact read must NOT block the control or fake a reassuring
      // zero: unshareWarning(null) keeps saying "checking", which is honest —
      // we genuinely do not know, and the numbers were only ever advisory.
      .catch(err => { if (live) console.error('[sharing] share-impact failed', err) })
    return () => { live = false }
  }, [slug, shared, loadImpact])

  if (!character) return null

  const name = character.label ?? character.id
  const rows = [
    ...characterPropertyRows(character, now),
    originRow(character),
    ...sharingPropertyRows(character),
  ]
  const typeLabel = STATUS[status]?.label ?? null

  const assignOwner = async (userId, email) => {
    setOwnerBusy(true)
    setOwnerError(null)
    try {
      await onAssignOwner(slug, userId, email)
    } catch (err) {
      // In place, not thrown to the app overlay: the server's message (403 for a
      // non-admin, 409 for clearing a private character's owner) is specific
      // enough to act on, and the panel is where the click happened.
      setOwnerError(err?.body?.error ?? err?.message ?? 'Could not assign the owner.')
    } finally {
      setOwnerBusy(false)
    }
  }

  const setShared = async next => {
    setBusy(true)
    setShareError(null)
    try {
      await onSetShared(slug, next)
    } catch (err) {
      // Surfaced in place rather than thrown to the app overlay: the panel is
      // where the action was taken, and the server's message (403 non-owner,
      // 409 ownerless) is specific enough to act on.
      setShareError(err?.body?.error ?? err?.message ?? 'Could not change visibility.')
    } finally {
      setBusy(false)
    }
  }

  return (
    <FloatingWindow
      id="character-properties"
      // The NAME, not "Properties": the bar is what identifies this window in a
      // stack of them, and the inner PROPERTIES header already labels the rows.
      title={name}
      onClose={onClose}
      // The window bar's ✕ inherits the testid the in-content button carried,
      // so the coverage that clicks it keeps working (the SettingsWorkspace
      // pattern — one close control, in the bar where a window's close lives).
      closeTestId="launcher-properties-close"
      dockable={false}
      defaultSize={{ w: 460, h: 620 }}
    >
      <div
        data-testid="launcher-properties-panel"
        className="aos-thin-scroll"
        style={{
          flex: 1, minHeight: 0, overflowY: 'auto',
          boxSizing: 'border-box', padding: '16px 18px 18px',
          background: 'var(--m-panel)', fontFamily: 'var(--m-font)', color: 'var(--m-fg)',
        }}
      >
        {/* ---- Header: which character this is about ---- */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 11, marginBottom: 14 }}>
          <PipelineGlyph status={status} size={30} />
          <div style={{ minWidth: 0, flex: 1 }}>
            <div style={{
              fontSize: 13, fontWeight: 500,
              whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
            }}>{name}</div>
            <code style={{ fontFamily: MONO, fontSize: 9, color: 'var(--m-fg-3)' }}>
              {character.id}{typeLabel ? ` · ${typeLabel}` : ''}
            </code>
          </div>
        </div>

        <div style={{
          fontFamily: UI_FONT, fontSize: 9, letterSpacing: '.14em',
          color: 'var(--m-fg-3)', marginBottom: 2,
        }}>PROPERTIES</div>

        {rows.map(row => <Row key={row.key} row={row} />)}

        {/* The two timestamps above are genuinely different measurements and the
            difference is not guessable from the labels alone, so it is stated
            once here rather than left for the reader to discover by noticing
            they disagree. */}
        <div style={{
          marginTop: 11, fontFamily: UI_FONT, fontSize: 9, lineHeight: 1.55,
          color: 'var(--m-fg-3)',
        }}>
          “Last activity” covers this character and all of its assets — animations,
          textures, materials, blueprints. “Config modified” moves only when the
          character’s own settings or rig change, so it can be much older.
        </div>

        {onAssignOwner && (
          <OwnerControl
            character={character}
            viewerId={viewerId}
            viewerEmail={viewerEmail}
            isOrgAdmin={isOrgAdmin}
            busy={ownerBusy}
            error={ownerError}
            onAssignOwner={assignOwner}
          />
        )}

        <BackdropControl slug={character.id} />

        {onSetShared && (
          <VisibilityControl
            character={character}
            viewerId={viewerId}
            isOrgAdmin={isOrgAdmin}
            impact={impact}
            busy={busy}
            error={shareError}
            onSetShared={setShared}
          />
        )}
      </div>
    </FloatingWindow>
  )
}
